import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { adminApi, clubsApi, championshipsApi, matchesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Settings,
  Users,
  Ban,
  CheckCircle,
  Trash2,
  Calendar,
  Swords,
  Save,
  X,
  Pencil,
} from 'lucide-react';

const Admin = () => {
  const { t, i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const { user, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Matches state
  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [dateInput, setDateInput] = useState('');
  const [savingDate, setSavingDate] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, clubsRes, champsRes] = await Promise.all([
          adminApi.getUsers(),
          clubsApi.list(),
          championshipsApi.list(),
        ]);
        setUsers(usersRes.data?.users || usersRes.data || []);
        setClubs(clubsRes.data || []);
        setChampionships(champsRes.data || []);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        toast.error('Errore nel caricamento dati admin');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleBanUser = async (userId, isBanned) => {
    try {
      await adminApi.updateUser(userId, { is_banned: !isBanned });
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === userId ? { ...u, is_banned: !isBanned } : u
        )
      );
      toast.success(isBanned ? 'User unbanned' : 'User banned');
    } catch (error) {
      toast.error('Errore aggiornamento utente');
    }
  };

  const handleDeleteUser = (userToDelete) => {
    setUserToDelete(userToDelete);
    setShowDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await adminApi.deleteUser(userToDelete.user_id);
      setUsers((prev) => prev.filter((u) => u.user_id !== userToDelete.user_id));
      toast.success(isItalian 
        ? `Utente "${userToDelete.name}" eliminato!` 
        : `User "${userToDelete.name}" deleted!`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      // Refresh clubs in case user was a captain
      const clubsRes = await clubsApi.list();
      setClubs(clubsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore eliminazione' : 'Delete error'));
    }
  };

  // --- Match handlers ---
  const handleSelectChampionship = async (champId) => {
    setSelectedChampionship(champId);
    if (!champId) { setMatches([]); return; }
    setLoadingMatches(true);
    try {
      const res = await championshipsApi.getFixtures(champId, null, { skip: 0, limit: 500 });
      const data = res.data;
      setMatches(data.matches || (Array.isArray(data) ? data : []));
    } catch (error) {
      toast.error(isItalian ? 'Errore caricamento partite' : 'Error loading matches');
      setMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleEditDate = (match) => {
    setEditingMatchId(match.match_id);
    setDateInput(match.match_date ? new Date(match.match_date).toISOString().slice(0, 16) : '');
  };

  const handleSaveDate = async (matchId) => {
    setSavingDate(true);
    try {
      await matchesApi.updateDate(matchId, dateInput || null);
      toast.success(isItalian ? 'Data e ora aggiornate!' : 'Date and time updated!');
      setMatches(prev => prev.map(m =>
        m.match_id === matchId ? { ...m, match_date: dateInput ? new Date(dateInput).toISOString() : null } : m
      ));
      setEditingMatchId(null);
      setDateInput('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore aggiornamento data' : 'Error updating date'));
    } finally {
      setSavingDate(false);
    }
  };

  const handleCancelEdit = () => { setEditingMatchId(null); setDateInput(''); };

  const formatMatchDate = (dateStr) => {
    if (!dateStr) return isItalian ? 'Non programmata' : 'Not scheduled';
    return new Date(dateStr).toLocaleString('it-IT', {
      weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3"
      >
        <Settings size={28} className="text-[#D500F9]" />
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
          {t('admin.title')}
        </h1>
      </motion.div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="bg-[#0a0d15] border border-white/10 p-1">
          <TabsTrigger
            value="users"
            className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white uppercase tracking-wider text-xs font-bold"
            data-testid="tab-users"
          >
            <Users size={14} className="mr-2" />
            {t('admin.users')}
          </TabsTrigger>
          <TabsTrigger
            value="matches"
            className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white uppercase tracking-wider text-xs font-bold"
            data-testid="tab-matches"
          >
            <Swords size={14} className="mr-2" />
            {isItalian ? 'Partite' : 'Matches'}
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-[#0a0d15] border border-white/10  overflow-hidden"
          >
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Gamertag</TableHead>
                  <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Email</TableHead>
                  <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Ruolo</TableHead>
                  <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Stato</TableHead>
                  <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} className="border-white/10 hover:bg-white/5">
                    <TableCell className="font-medium text-white">
                      {u.gamertag ? (
                        <span className="text-[#00F0FF]">{u.gamertag}</span>
                      ) : (
                        <span className="text-slate-400">{u.name}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-400">{u.email}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 text-xs rounded uppercase tracking-wider ${
                          u.role === 'super_admin'
                            ? 'bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/30'
                            : u.role === 'admin'
                            ? 'bg-[#D500F9]/20 text-[#D500F9] border border-[#D500F9]/30'
                            : u.role === 'captain'
                            ? 'bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30'
                            : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                        }`}
                      >
                        {u.role === 'super_admin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : u.role === 'captain' ? 'Capitano' : 'Giocatore'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {u.is_banned ? (
                        <span className="px-2 py-1 text-xs rounded bg-[#FF1744]/20 text-[#FF1744] border border-[#FF1744]/30">
                          Bannato
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30">
                          Attivo
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {/* Hide ban button for super_admin and admin users unless current user is super_admin */}
                        {(isSuperAdmin || !['super_admin', 'admin'].includes(u.role)) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleBanUser(u.user_id, u.is_banned)}
                            className={u.is_banned ? 'text-[#00E676]' : 'text-[#FF1744]'}
                            data-testid={`ban-user-${u.user_id}`}
                          >
                            {u.is_banned ? <CheckCircle size={16} /> : <Ban size={16} />}
                          </Button>
                        )}
                        {/* Delete button - hidden for super_admin and self */}
                        {u.user_id !== user?.user_id && (isSuperAdmin || !['super_admin', 'admin'].includes(u.role)) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteUser(u)}
                            className="text-slate-500 hover:text-[#FF1744]"
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </motion.div>
        </TabsContent>

        {/* Matches Tab */}
        <TabsContent value="matches">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {/* Championship selector */}
            <div className="flex items-center gap-3 bg-[#0a0d15] border border-white/10 p-4 rounded">
              <Calendar size={18} className="text-[#00F0FF]" />
              <span className="text-sm text-slate-400 font-medium uppercase tracking-wider">
                {isItalian ? 'Campionato' : 'Championship'}:
              </span>
              <Select value={selectedChampionship || ''} onValueChange={handleSelectChampionship}>
                <SelectTrigger className="w-64 bg-[#0d1117] border-white/10 text-white" data-testid="select-championship">
                  <SelectValue placeholder={isItalian ? 'Seleziona campionato...' : 'Select championship...'} />
                </SelectTrigger>
                <SelectContent className="bg-[#0d1117] border-white/10">
                  {championships.map(c => (
                    <SelectItem key={c.championship_id} value={c.championship_id} className="text-white hover:bg-white/10">
                      {c.name} ({c.season})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Matches list */}
            {!selectedChampionship ? (
              <div className="text-center py-12 text-slate-500">
                <Swords size={40} className="mx-auto mb-3 opacity-30" />
                <p>{isItalian ? 'Seleziona un campionato per vedere le partite' : 'Select a championship to see matches'}</p>
              </div>
            ) : loadingMatches ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
              </div>
            ) : matches.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <p>{isItalian ? 'Nessuna partita trovata per questo campionato' : 'No matches found for this championship'}</p>
              </div>
            ) : (
              <div className="bg-[#0a0d15] border border-white/10 overflow-hidden rounded">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">G.</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Partita' : 'Match'}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Stato' : 'Status'}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Data e Ora' : 'Date & Time'}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">{isItalian ? 'Azioni' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matches.map((match) => (
                      <TableRow key={match.match_id} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-slate-500 text-sm font-mono">{match.match_day || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm">{match.home_club?.name || match.home_club_name || 'TBD'}</span>
                            <span className="text-slate-600 text-xs">vs</span>
                            <span className="text-white font-medium text-sm">{match.away_club?.name || match.away_club_name || 'TBD'}</span>
                          </div>
                          {match.status === 'completed' && (
                            <span className="text-xs text-[#00F0FF]">
                              {match.home_score ?? 0} - {match.away_score ?? 0}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs rounded ${
                            match.status === 'completed'
                              ? 'bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30'
                              : match.status === 'scheduled'
                              ? 'bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/30'
                              : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                          }`}>
                            {match.status === 'completed' ? (isItalian ? 'Completata' : 'Completed')
                              : match.status === 'scheduled' ? (isItalian ? 'Programmata' : 'Scheduled')
                              : match.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          {editingMatchId === match.match_id ? (
                            <Input
                              type="datetime-local"
                              value={dateInput}
                              onChange={(e) => setDateInput(e.target.value)}
                              className="w-52 bg-[#0d1117] border-[#00F0FF]/50 text-white text-sm"
                              data-testid={`date-input-${match.match_id}`}
                            />
                          ) : (
                            <span className={`text-sm ${match.match_date ? 'text-white' : 'text-slate-600 italic'}`}>
                              {formatMatchDate(match.match_date)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {editingMatchId === match.match_id ? (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => handleSaveDate(match.match_id)}
                                disabled={savingDate}
                                className="text-[#00E676] hover:text-[#00E676]/80"
                                data-testid={`save-date-${match.match_id}`}
                              >
                                <Save size={16} />
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                onClick={handleCancelEdit}
                                className="text-slate-500 hover:text-white"
                                data-testid={`cancel-date-${match.match_id}`}
                              >
                                <X size={16} />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => handleEditDate(match)}
                              className="text-[#00F0FF] hover:text-[#00F0FF]/80"
                              data-testid={`edit-date-${match.match_id}`}
                            >
                              <Pencil size={14} className="mr-1" />
                              <span className="text-xs">{isItalian ? 'Imposta' : 'Set'}</span>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider text-[#FF1744]">
              {isItalian ? 'Elimina Utente' : 'Delete User'}
            </DialogTitle>
          </DialogHeader>
          
          {userToDelete && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-[#FF1744]/10 border border-[#FF1744]/30 ">
                <div className="text-center">
                  <p className="font-medium text-white">{userToDelete.name}</p>
                  {userToDelete.gamertag && (
                    <p className="text-sm text-[#00F0FF]">{userToDelete.gamertag}</p>
                  )}
                  <p className="text-xs text-slate-500">{userToDelete.email}</p>
                </div>
                <p className="text-sm text-slate-400 text-center mt-3">
                  {isItalian 
                    ? 'Questa azione è irreversibile. Tutti i dati dell\'utente verranno eliminati.'
                    : 'This action cannot be undone. All user data will be deleted.'}
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-white/10"
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setUserToDelete(null);
                  }}
                >
                  {isItalian ? 'Annulla' : 'Cancel'}
                </Button>
                <Button
                  className="flex-1 bg-[#FF1744] hover:bg-[#FF1744]/80"
                  onClick={confirmDeleteUser}
                  data-testid="confirm-delete-user-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  {isItalian ? 'Elimina' : 'Delete'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
